// El latido de cambios (2026-09-10): "¿qué ha cambiado en lo que tengo
// delante?". Cada pocos segundos, SOLO con la app visible y con sesión, se
// pregunta al servidor por las cargas y fechas que la pantalla enseña. El
// servidor devuelve identificadores y quién; la pantalla vuelve a pedir sus
// datos por las lecturas de siempre, en silencio (sin spinner ni parpadeo).
//
// Es un aviso, no el dato: si esto falla, la app sigue exactamente igual que
// antes (refresco manual, pull-to-refresh, visibilidad). Un fallo del
// servidor espacia el latido a 30 s; un 403 (perfil sin acceso) lo apaga.
import { api } from './api';
import { getToken, isTokenValid } from './storage';
import { isOfflineForUser } from './networkStatus';

export interface ChangeFeedItem {
  kind: 'Planning' | 'Cargo' | 'CargoDetail' | string;
  action: 'Added' | 'Modified' | 'Deleted' | string;
  idPlanning?: number | null;
  idCargo?: number | null;
  idCargoDetail?: number | null;
  date?: string | null;
  userName?: string | null;
  atUtc: string;
}

export interface ChangeFeedPage {
  cursor: number;
  reset: boolean;
  pollSeconds: number;
  changes: ChangeFeedItem[];
}

/** Lo que la pantalla tiene delante. Se lee en CADA latido, así puede cambiar sin reiniciar nada. */
export interface ChangeFeedWatch {
  cargoIds?: number[];
  dates?: Date[];
  planningIds?: number[];
}

export interface ChangeFeedHandle {
  stop: () => void;
  /** Adelanta el siguiente latido (p. ej. al volver a primer plano). */
  poke: () => void;
}

const DEFAULT_POLL_SECONDS = 5;
const BACKOFF_SECONDS = 30;

// El cursor es de la sesión, no de la pantalla: al cambiar de página no se
// vuelve a empezar (se perderían los cambios de mientras).
let cursor = 0;

export function resetChangeFeedCursor(): void {
  cursor = 0;
}

function isVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState === 'visible';
}

function toDateKey(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function startChangeFeed(
  getWatch: () => ChangeFeedWatch,
  onPage: (page: ChangeFeedPage) => void,
): ChangeFeedHandle {
  let running = true;
  let inFlight = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pollSeconds = DEFAULT_POLL_SECONDS;

  const schedule = (seconds: number) => {
    if (!running) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(beat, Math.max(0.2, seconds) * 1000);
  };

  const beat = async () => {
    if (!running) return;
    // Sin sesión, sin red o en segundo plano no se pregunta: gastaría batería
    // por nada. Se vuelve a intentar al ritmo normal.
    if (!isVisible() || !getToken() || !isTokenValid() || isOfflineForUser()) {
      schedule(pollSeconds);
      return;
    }
    if (inFlight) { schedule(pollSeconds); return; }

    const watch = getWatch() || {};
    const body = {
      cursor,
      cargoIds: (watch.cargoIds || []).filter((id) => Number.isInteger(id) && id > 0),
      dates: (watch.dates || []).map(toDateKey),
      planningIds: (watch.planningIds || []).filter((id) => Number.isInteger(id) && id > 0),
    };

    inFlight = true;
    try {
      const res = await api.post<ChangeFeedPage>('api/changes/sync', body, { timeout: 10000 });
      inFlight = false;
      if (!running) return;
      const page = res.data;
      if (page && typeof page.cursor === 'number') cursor = page.cursor;
      if (page && page.pollSeconds > 0) pollSeconds = page.pollSeconds;
      if (page && (page.reset || (page.changes && page.changes.length > 0))) {
        try { onPage(page); } catch { /* la pantalla ya se apañará */ }
      }
      schedule(pollSeconds);
    } catch (e: any) {
      inFlight = false;
      if (!running) return;
      if (e?.response?.status === 403) { running = false; return; }
      schedule(BACKOFF_SECONDS);
    }
  };

  const onVisibility = () => {
    if (isVisible()) schedule(0.2);
  };
  const doc = typeof document !== 'undefined' ? document : null;
  doc?.addEventListener('visibilitychange', onVisibility);
  schedule(0.2);

  return {
    stop: () => {
      running = false;
      if (timer) clearTimeout(timer);
      doc?.removeEventListener('visibilitychange', onVisibility);
    },
    poke: () => schedule(0.2),
  };
}

/** El último que tocó algo, para el aviso "Actualizado por…". Null si no se sabe. */
export function lastEditorOf(page: ChangeFeedPage | null | undefined): string | null {
  const withUser = (page?.changes || []).filter((c) => (c.userName || '').trim() !== '');
  if (withUser.length === 0) return null;
  return withUser.reduce((a, b) => (a.atUtc >= b.atUtc ? a : b)).userName!.trim();
}
